"use client";

import { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import PhoneInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";
import { functions, signInWithPhoneCustomToken } from "@/lib/firebase";
import { useTranslations } from "@/i18n/LocaleProvider";
import { isDemoMode } from "@/lib/demoMode";

type Stage = "enter" | "sent" | "verified";

interface SendPhoneOtpResponse {
  sent: boolean;
}

interface VerifyPhoneOtpResponse {
  customToken: string;
}

/**
 * Phone verification via WhatsApp OTP, shared by the booking flow, batch enrollment, and
 * /account -- all three now require a verified phone before proceeding, and verifying IS
 * signing in (see functions/src/otp.ts's verifyPhoneOtpAndSignIn), so this component owns both
 * the OTP round-trip and the resulting Firebase sign-in, not just the UI for entering a code.
 *
 * Controlled on `phone` so the caller can prefill it into a booking/account form and read it
 * back; `onVerified` fires once, after the code checks out and the custom-token sign-in
 * actually completes.
 */
export default function PhoneOtpAuth({
  phone,
  onPhoneChange,
  onVerified,
  phoneLabel,
  bordered = true,
  initiallyVerified = false,
}: {
  phone: string;
  onPhoneChange: (value: string) => void;
  onVerified: () => void;
  phoneLabel?: string;
  // false when a parent (DetailsStep, BatchDetailsStep) already supplies its own surface-card
  // around this whole form -- avoids a card nested inside a card.
  bordered?: boolean;
  // True when a parent restored a previously-verified phone from a saved draft (see
  // draftStorage.ts) -- only read once, at mount, so this component opens straight to the
  // "verified" checkmark instead of making a customer re-enter a number they already confirmed.
  initiallyVerified?: boolean;
}) {
  const { t } = useTranslations();
  const [stage, setStage] = useState<Stage>(initiallyVerified ? "verified" : "enter");
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A plain boolean flipped by a timeout, not a stored timestamp compared against Date.now()
  // at render time -- calling Date.now() during render is an impurity React's rules flag, and
  // for good reason: it'd make this component's output depend on when it happens to re-render,
  // not just its props/state.
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (canResend || stage !== "sent") return;
    const timer = setTimeout(() => setCanResend(true), 60_000); // matches the server's 60s minimum resend gap
    return () => clearTimeout(timer);
  }, [canResend, stage]);

  async function sendCode() {
    if (!phone.trim() || !consent) return;
    setLoading(true);
    setError(null);
    try {
      const sendPhoneOtp = httpsCallable<
        { phone: string; contactConsent: true },
        SendPhoneOtpResponse
      >(functions, "sendPhoneOtp");
      await sendPhoneOtp({ phone, contactConsent: true });
      setStage("sent");
      setCanResend(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("phoneVerification.sendError"));
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode() {
    if (code.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const verifyPhoneOtp = httpsCallable<
        { phone: string; code: string },
        VerifyPhoneOtpResponse
      >(functions, "verifyPhoneOtp");
      const result = await verifyPhoneOtp({ phone, code });
      await signInWithPhoneCustomToken(result.data.customToken);
      setStage("verified");
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("phoneVerification.verifyError"));
    } finally {
      setLoading(false);
    }
  }

  if (stage === "verified") {
    return (
      <p className="flex items-center gap-2 text-[0.95rem] font-medium text-accent">
        <span aria-hidden className="text-lg leading-none">✓</span>
        {t("phoneVerification.verified")}
      </p>
    );
  }

  return (
    <div className={bordered ? "surface-card space-y-2.5 p-4 sm:space-y-3 sm:p-5" : "space-y-2.5 sm:space-y-3"}>
      {stage === "enter" ? (
        <div className="space-y-1.5">
          <label htmlFor="phone-otp-input" className="text-[0.8rem] font-medium tracking-wide text-foreground/70">
            {phoneLabel ?? t("phoneVerification.phoneLabel")}
          </label>
          <PhoneInput
            id="phone-otp-input"
            international
            defaultCountry="IN"
            countryCallingCodeEditable={false}
            value={phone}
            onChange={(value) => onPhoneChange(value ?? "")}
            className="phone-input"
            numberInputProps={{ required: true }}
            // Bundles every country's flag as an inline SVG instead of the library's default of
            // loading each one from purecatamphetamine.github.io as an <img> -- this app's CSP
            // deliberately doesn't trust arbitrary third-party image hosts on a page that also
            // handles the OTP that gates account access, and the ~44kB gzip cost is trivial next
            // to that.
            flags={flags}
          />
        </div>
      ) : (
        // A static line, not a disabled PhoneInput -- disabling the library's own multi-part
        // control (country <select> plus number field) while it's mounted was corrupting its
        // displayed value to an unrelated country/number on the very next render, found by
        // actually driving this through the browser rather than trusting it looked right.
        <p className="text-[0.8rem] font-medium tracking-wide text-foreground/70">
          {phoneLabel ?? t("phoneVerification.phoneLabel")}
          <span className="ml-2 text-[0.925rem] font-normal tracking-normal text-foreground">
            {phone}
          </span>
        </p>
      )}

      {stage === "enter" && (
        <>
          <label className="flex items-start gap-3 text-[0.925rem] leading-snug text-foreground/90">
            <input
              required
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 size-4 accent-accent"
            />
            <span>{t("phoneVerification.consent")}</span>
          </label>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => void sendCode()}
              disabled={loading || !phone.trim() || !consent}
              className="rounded-full bg-accent px-6 py-2.5 text-[0.925rem] font-semibold tracking-wide text-accent-foreground transition-opacity disabled:opacity-50"
            >
              {loading ? t("phoneVerification.sending") : t("phoneVerification.sendCode")}
            </button>
          </div>
        </>
      )}

      {stage === "sent" && (
        <>
          <p className="text-[0.925rem] text-muted">{t("phoneVerification.codeSentNotice")}</p>
          {isDemoMode && (
            <p className="text-[0.8rem] font-medium text-accent">
              Demo mode: any 6-digit code works.
            </p>
          )}
          <div className="space-y-1.5">
            <label htmlFor="phone-otp-code" className="text-[0.8rem] font-medium tracking-wide text-foreground/70">
              {t("phoneVerification.codeLabel")}
            </label>
            <input
              id="phone-otp-code"
              required
              inputMode="numeric"
              maxLength={6}
              placeholder={t("phoneVerification.codePlaceholder")}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full rounded-lg border border-foreground/20 px-4 py-3 text-lg font-medium tracking-[0.4em] focus:border-accent focus:outline-none focus:ring-[3px] focus:ring-accent/20"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => void verifyCode()}
              disabled={loading || code.length !== 6}
              className="rounded-full bg-accent px-6 py-2.5 text-[0.925rem] font-semibold tracking-wide text-accent-foreground transition-opacity disabled:opacity-50"
            >
              {loading ? t("phoneVerification.verifying") : t("phoneVerification.verifyCode")}
            </button>
            <button
              type="button"
              onClick={() => void sendCode()}
              disabled={loading || !canResend}
              className="text-[0.875rem] font-medium text-accent underline decoration-accent/40 underline-offset-4 disabled:text-foreground/40 disabled:no-underline"
            >
              {t("phoneVerification.resend")}
            </button>
          </div>
        </>
      )}

      {error && <p className="text-[0.875rem] text-red-700">{error}</p>}
    </div>
  );
}
